/* eslint-disable */
import { QuestionCircleOutlined } from '@ant-design/icons';
import { message, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, {
  useEffect,
  useMemo, useRef, useState
} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  FormProvider, useFieldArray, useForm, useWatch
} from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { Label, Radio, Typography } from 'tfc-components';

import BillingInformation from './BillingInformation';
import ChoosePackage from './ChoosePackage';
import Confirmation from './Confirmation';
import PackageSelector from './PackageSelector';

import Icon from 'components/atoms/Icon';
import Loading from 'components/atoms/Loading';
import FooterCheckout from 'components/organisms/FooterCheckout';
import Section from 'components/organisms/Section';

import { checkoutRetryService, checkoutService } from 'services/accounts';
import { getAddOnPackagesService, getMainPackagesService } from 'services/servicePackages';
import { ICheckoutRequest, ServicePackageAddOnData } from 'services/servicePackages/type';
import mapModifiers from 'utils/functions';

export interface PackageOption {
  id: string;
  name: string;
  amount: number;
  duration: number;
  description?: string;
  servicePackageAddOnData?: ServicePackageAddOnData[];
  latestTier: {
    numberOfAgent: number,
    numberOfFile: number,
    totalSizeLimit: number,
    numberOfWebsite: number,
    numberOfSession: number
  },
  numberOfAgent?: number,
  numberOfFile?: number,
  totalSizeLimit?: number,
  numberOfWebsite?: number,
  numberOfSession?: number
}

export type InvoiceType = 'business' | 'individual';

export interface IBillingInfo {
  fullName: string;
  phone: string;
  email: string;
  invoiceType: InvoiceType,
  invoice: IBusinessInfo | IIndividualInfo;
}

export interface IBusinessInfo {
  taxId: string;
  companyName: string;
  fullAddress: string;
  invoiceEmail?: string[];
}

export interface IIndividualInfo {
  nationalId: string;
  fullName: string;
  fullAddress: string;
  invoiceEmail?: string[];
}
export interface IPackage {
  id: string;
  name: string;
  duration: number;
  price: number;
  quantity: number;
  packageId?: string;
  latestTier?: {
    numberOfAgent: number;
    numberOfFile: number;
    totalSizeLimit: number;
    numberOfWebsite: number;
    numberOfSession: number;
  };
}

export interface PackageForm {
  packages: IPackage | null;
  addOns: IPackage[]
}

const RETURN_URL: string = `${window.location.origin}/checkout/payment-status/success`;
const CANCEL_URL: string = `${window.location.origin}/checkout/payment-status/cancel`;

const Checkout: React.FC = () => {
  const [step, setStep] = useState(1);
  const [searchParams] = useSearchParams();
  const planName = searchParams.get('planName');
  const tokenRetry = searchParams.get('token');
  const [isGoBack, setIsGoBack] = useState(false);
  const [billingType, setBillingType] = useState<'one-time' | 'subscription'>('subscription');
  const [duration, setDuration] = useState<'monthly' | 'yearly'>('yearly');

  const billingFormSubmitRef = useRef<(() => void) | null>(null);
  const [billingInfoData, setBillingInfoData] = useState<IBillingInfo | null>(null);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  /* Form */
  const packageMethod = useForm<PackageForm>({
    defaultValues: {
      packages: null,
      addOns: [],
    },
    mode: 'onChange'
  });

  const { fields: addOnsPackage, append: appendAddOn, remove: removeAddOn } = useFieldArray({
    control: packageMethod.control,
    name: 'addOns',
  });

  const formWatch = useWatch({
    control: packageMethod.control
  });

  const { data: mainPackagesData, isLoading: isMainLoading } = useQuery({
    queryKey: ['getMainPackages'],
    queryFn: getMainPackagesService,
  });

  const { data: addonPackagesData, isLoading: isAddonLoading } = useQuery({
    queryKey: ['getAddOnPackages'],
    queryFn: getAddOnPackagesService,
  });

  const mainPackages: PackageOption[] = useMemo(() => {
    if (!mainPackagesData) return [];
    return mainPackagesData.data
      .map((pkg) => {
        if (!pkg.servicePackageData || typeof pkg.servicePackageData.id === 'undefined') {
          return {};
        }
        const id = `main-${pkg.servicePackageData.id}`;
        return {
          id,
          name: pkg.servicePackageData.name || 'Unknown Package',
          duration: pkg.servicePackageData.duration || 0,
          amount: pkg.servicePackageData.price || 0,
          servicePackageAddOnData: pkg.servicePackageData.servicePackageAddOnData,
          latestTier: pkg?.servicePackageData?.latestTier ?? {
            numberOfAgent: pkg?.servicePackageData?.numberOfAgent,
            numberOfFile: pkg?.servicePackageData?.numberOfFile,
            numberOfSession: pkg?.servicePackageData?.numberOfSession,
            numberOfWebsite: pkg?.servicePackageData?.numberOfWebsite,
          }
        };
      })
      .filter((pkg): pkg is PackageOption => pkg !== null);
  }, [mainPackagesData]);

  const addonPackages: PackageOption[] = useMemo(() => {
    if (!addonPackagesData) return [];
    return addonPackagesData.data.map((pkg) => ({
      id: `addon-${pkg.servicePackageAddOnData.id}`,
      name: pkg.servicePackageAddOnData.name,
      amount: pkg.servicePackageAddOnData.price,
      duration: pkg.servicePackageAddOnData.duration,
      description: pkg.servicePackageAddOnData.description || undefined,
      latestTier: pkg?.servicePackageAddOnData.latestTier
    }));
  }, [addonPackagesData]);

  const mainPackagesByDuration = useMemo(() => mainPackages?.filter(
    (item) => (item.duration === (duration === 'yearly' ? 3 : 2))
  ), [duration, mainPackages]);

  const { data: checkoutRetryData, isLoading: isCheckoutRetryLoading } = useQuery({
    queryKey: ['checkoutRetry'],
    queryFn: () => checkoutRetryService(tokenRetry || ''),
    enabled: !!tokenRetry && !isMainLoading,
    onSuccess: (data) => {
      if (data) {
        const { invoiceData } = data;
        setBillingInfoData({
          fullName: data.buyerData.fullName,
          phone: data.buyerData.phone,
          email: data.buyerData.email,
          invoiceType: invoiceData.invoiceType === 2 ? 'business' : 'individual',
          invoice: invoiceData.invoiceType === 2
            ? {
              taxId: invoiceData.taxCode,
              companyName: invoiceData.companyName,
              fullAddress: invoiceData.fullAddress,
              invoiceEmail: invoiceData.invoiceEmails,
            }
            : {
              nationalId: invoiceData.taxCode,
              fullName: invoiceData.companyName,
              fullAddress: invoiceData.fullAddress,
              invoiceEmail: invoiceData.invoiceEmails,
            },
        });

        const mainPackageRetry = mainPackages?.find(
          (pkg) => pkg.id === `main-${data.packageData.servicePackageId}`
        )!;
        packageMethod.setValue('packages', {
          ...mainPackageRetry, quantity: data?.packageData?.quantity, duration: data?.packageData?.duration || 1, price: mainPackageRetry?.amount,
          latestTier: {
            numberOfAgent: mainPackageRetry?.latestTier.numberOfAgent,
            numberOfSession: mainPackageRetry?.latestTier.numberOfSession,
            numberOfFile: mainPackageRetry?.latestTier.numberOfFile,
            numberOfWebsite: mainPackageRetry?.latestTier.numberOfWebsite,
            totalSizeLimit: mainPackageRetry?.latestTier.totalSizeLimit,
          }
        }, { shouldDirty: true, shouldValidate: true });


        //addOns

        // Map addon id to addon object for quick lookup
        const addonMap = new Map<string, typeof addonPackages[number]>();

        addonPackages.forEach((addon) => {
          // addon.id = 'addon-7' => extract number part to match `servicePackageAddOnId`
          const match = addon.id.match(/^addon-(\d+)$/);
          if (match) {
            addonMap.set(match[1], addon); // e.g. "7" => { id: 'addon-7', ... }
          }
        });

        const enrichedAddOns = data.packageData.servicePackageAddOns.map((item) => {
          const matchedAddon = addonPackages.find((addon) =>
            addon.id === `addon-${item.servicePackageAddOnId}`
          );

          return {
            id: `addon-${item.servicePackageAddOnId}`,
            name: matchedAddon?.name ?? 'Unknown Addon',
            price: matchedAddon?.amount ?? 0,
            duration: item.quantity ?? 1,
            quantity: item.unit ?? 1,
            packageId: String(item.servicePackageAddOnId),
            latestTier: matchedAddon?.latestTier ?? {
              numberOfAgent: item?.numberOfAgent ?? 0,
              numberOfFile: item?.numberOfFile ?? 0,
              numberOfSession: item?.numberOfSession ?? 0,
              numberOfWebsite: item?.numberOfWebsite ?? 0,
              totalSizeLimit: item?.totalSizeLimit ?? 0
            }
          };
        });
        packageMethod.setValue("addOns", enrichedAddOns)

        setBillingType(data.packageData.billingType === 2 ? 'subscription' : 'one-time');
        setDuration(mainPackageRetry.duration === 3 ? 'yearly' : 'monthly');

        setIsGoBack(true);
        setStep(3);
      }
    },
  });

  const addOns = useWatch({ control: packageMethod.control, name: 'addOns' });
  const mainPackage = useWatch({ control: packageMethod.control, name: 'packages' });

  const total =
    (addOns?.reduce((acc, pkg) => acc + (pkg.price ?? 0) * (pkg.quantity || 0) * (pkg.duration || 0), 0) ?? 0) +
    ((mainPackage?.price ?? 0) * (mainPackage?.duration ?? 0) * (mainPackage?.quantity ?? 0));

  function totalTiers() {
    const totals = {
      numberOfAgent: 0,
      numberOfFile: 0,
      numberOfSession: 0,
      numberOfWebsite: 0,
      totalSizeLimit: 0
    };

    // Xử lý dữ liệu từ mảng: lặp qua từng phần tử trong mảng
    if (Array.isArray(addOns)) { // Kiểm tra xem dataArray có phải là một mảng hợp lệ không
      addOns.forEach(item => {
        // Đảm bảo phần tử hiện tại và thuộc tính latestTier của nó tồn tại
        if (item && item.latestTier) {
          const tier = item.latestTier;
          // Cộng dồn các giá trị, sử dụng || 0 để xử lý trường hợp thiếu key hoặc giá trị null/undefined
          totals.numberOfAgent += tier.numberOfAgent * item.duration * item.quantity || 0;
          totals.numberOfFile += tier.numberOfFile * item.duration * item.quantity || 0;
          totals.numberOfSession += tier.numberOfSession * item.duration * item.quantity || 0;
          totals.numberOfWebsite += tier.numberOfWebsite * item.duration * item.quantity || 0;
          totals.totalSizeLimit += tier.totalSizeLimit * item.duration * item.quantity || 0;
        }
      });
    }

    // Xử lý dữ liệu từ đối tượng riêng lẻ
    if (mainPackage && mainPackage.latestTier) {
      const tier2 = mainPackage.latestTier;
      totals.numberOfAgent += tier2.numberOfAgent * mainPackage.duration * mainPackage.quantity || 0;
      totals.numberOfFile += tier2.numberOfFile * mainPackage.duration * mainPackage.quantity || 0;
      totals.numberOfSession += tier2.numberOfSession * mainPackage.duration * mainPackage.quantity || 0;
      totals.numberOfWebsite += tier2.numberOfWebsite * mainPackage.duration * mainPackage.quantity || 0;
      totals.totalSizeLimit += tier2.totalSizeLimit * mainPackage.duration * mainPackage.quantity || 0;
    }

    return totals;
  }

  const handleSubmitDataStep1 = () => {
    setStep(2);
  };

  const handleSubmitDataStep2 = (data: IBillingInfo) => {
    setBillingInfoData(data);
    setStep(3);
  };

  const handleActionBack = () => {
    setStep(step - 1);
    setIsGoBack(true);
    setNextBtnDisabled(false);
  };

  const handleAction = () => {
    if (step === 1) {
      handleSubmitDataStep1();
    } else if (step === 2 && billingFormSubmitRef.current) {
      billingFormSubmitRef.current();
    }
  };

  const { mutate: checkoutMutate } = useMutation(
    ['checkout'],
    (data: ICheckoutRequest) => checkoutService(data),
    {
      onSuccess(data) {
        localStorage.setItem('paymentChecked', data.code);
        window.location.href = (data.link);
      },
      onError() {
        message.error('Something went wrong, please try again later');
      },
    }
  );

  const handleCheckout = async () => {
    if (!billingInfoData) return;
    if (!recaptchaRef.current) return;
    try {
      const token = await recaptchaRef.current.executeAsync();
      if (!token) return;
      let checkoutDataFull: ICheckoutRequest;
      if (checkoutRetryData && tokenRetry) {
        checkoutDataFull = {
          packageData: checkoutRetryData.packageData,
          buyerData: checkoutRetryData.buyerData,
          invoiceData: checkoutRetryData.invoiceData,
          paymentMethod: checkoutRetryData.paymentMethod,
          returnUrl: RETURN_URL,
          cancelUrl: CANCEL_URL,
        };
      } else {
        checkoutDataFull = {
          packageData: {
            billingType: billingType === 'subscription' ? 2 : 1,
            servicePackageId: parseInt((mainPackage?.id ?? '').replace('main-', ''), 10),
            quantity: mainPackage?.duration ?? 1,
            servicePackageAddOns: addOns.map((addon) => ({
              servicePackageAddOnId: parseInt(addon.id.replace('addon-', ''), 10),
              quantity: addon?.duration ?? 1,
              unit: addon?.quantity ?? 1
            })),
          },
          buyerData: {
            fullName: billingInfoData.fullName,
            phone: billingInfoData.phone,
            email: billingInfoData.email,
          },
          invoiceData: {
            invoiceType: billingInfoData.invoiceType === ('business' as InvoiceType) ? 2 : 1,
            taxCode: billingInfoData.invoiceType === ('business' as InvoiceType) ? (billingInfoData.invoice as IBusinessInfo).taxId : (billingInfoData.invoice as IIndividualInfo).nationalId,
            companyName: billingInfoData.invoiceType === ('business' as InvoiceType) ? (billingInfoData.invoice as IBusinessInfo).companyName : (billingInfoData.invoice as IIndividualInfo).fullName,
            fullAddress: billingInfoData.invoice.fullAddress,
            invoiceEmails: billingInfoData.invoice.invoiceEmail || [],
          },
          paymentMethod: 1,
          returnUrl: RETURN_URL,
          cancelUrl: CANCEL_URL,
        };
      }
      checkoutMutate({ ...checkoutDataFull, ggRecaptchaToken: token });
    } catch (error) {
      message.error('Something went wrong, please try again later');
    } finally {
      recaptchaRef.current.reset();
      localStorage.removeItem('paymentChecked');
    }
  };

  useEffect(() => {
    if (!isMainLoading && mainPackages?.length && !mainPackage?.id) {
      const defaultMain = mainPackagesByDuration && mainPackagesByDuration
        .find((pkg) => pkg.name === planName);
      if (defaultMain) {
        packageMethod.setValue("packages", {
          ...defaultMain,
          price: defaultMain.amount,
          quantity: 1,
          duration: 1
        })
        // setSelectedMain({ id: defaultMain.id, name: defaultMain.name });
        // setBasePrice((prev) => ({ ...prev, [defaultMain.id]: defaultMain.amount }));
        // setQuantity((prev) => ({ ...prev, [defaultMain.id]: 1 }));
      }
    }
  }, [mainPackages, isMainLoading, mainPackage]);

  return isCheckoutRetryLoading ? <div className="p-checkout_paymentStatus_loading"><Loading /></div> : (
    <>
      <Section>
        <div className="p-checkout">
          <div className="p-checkout_steps">
            {[1, 2, 3].map((val) => (
              <React.Fragment key={val}>
                <div className={mapModifiers('p-checkout_steps_step', step === val ? 'active' : '')}>
                  <div className={mapModifiers('p-checkout_steps_number', step > val ? 'active' : '')}>
                    {step > val ? <Icon iconName="checkWhite" size="20" /> : <Typography.Text extendClasses="p-checkout_steps_numberText fs-20x24">{val}</Typography.Text>}
                  </div>
                  <Typography.Text extendClasses="p-checkout_steps_title fs-20x24">
                    {val === 1 ? 'Choose Package' : val === 2 ? 'Billing Information' : 'Confirmation'}
                  </Typography.Text>
                </div>
                {val < 3 && <div className="p-checkout_steps_divider" />}
              </React.Fragment>
            ))}
          </div>

          <div className="p-checkout_content">
            {step === 1 && (
              <>
                <FormProvider {...packageMethod}>
                  <ChoosePackage
                    billingType={billingType}
                    total={total}
                    duration={duration}
                    visiblePackages={addOnsPackage}
                    mainPackage={mainPackage}
                    method={packageMethod}
                  />
                </FormProvider>

                <div className="p-checkout_billingType p-checkout_mt32">
                  <Typography.Text fontweight="600" extendClasses="p-checkout_titleProduct fs-20x24">Billing type</Typography.Text>
                  <div className="p-checkout_billingType_options">
                    <Label className="p-checkout_billingType_option">
                      <Radio
                        size={20}
                        checked={billingType === 'one-time'}
                        onChange={() => setBillingType('one-time')}
                      />
                      <Typography.Text extendClasses="p-checkout_billingType_optionText">
                        One-time purchase
                        {' '}
                        <Tooltip placement="bottomRight" title="When you choose onetime purchase, the minimum duration for monthly packages is 3 months.">
                          <span><QuestionCircleOutlined /></span>
                        </Tooltip>
                      </Typography.Text>
                    </Label>
                    <Label className="p-checkout_billingType_option">
                      <Radio
                        size={20}
                        checked={billingType === 'subscription'}
                        onChange={() => setBillingType('subscription')}
                      />
                      <Typography.Text extendClasses="p-checkout_billingType_optionText">Subscription</Typography.Text>
                    </Label>
                  </div>
                </div>

                <PackageSelector
                  mainPackages={mainPackagesByDuration || []}
                  isAddonPackageLoading={isAddonLoading}
                  isMainPackageLoading={isMainLoading}
                  selectedMain={formWatch.packages as IPackage}
                  selectedAddons={addOnsPackage}
                  setSelectedMain={(data) => {
                    packageMethod.setValue('packages', data, { shouldDirty: true, shouldValidate: true });
                  }}
                  setSelectedAddons={(data) => {
                    if (data) {
                      const foundIndex = addOnsPackage.findIndex((f) => f.packageId === data.id);
                      if (foundIndex === -1) {
                        appendAddOn({ ...data, packageId: data?.id });
                      } else {
                        removeAddOn(foundIndex);
                      }
                    }
                  }}
                  duration={duration}
                  setDuration={setDuration}
                  isGoBack={isGoBack}
                  setIsGoBack={setIsGoBack}
                  method={packageMethod}
                />
              </>
            )}
            {step === 2 && (
              <BillingInformation
                onSubmit={handleSubmitDataStep2}
                triggerSubmitRef={billingFormSubmitRef}
                initialData={billingInfoData || undefined}
              />
            )}
            {step === 3 && (
              <Confirmation
                billingType={billingType}
                billingData={billingInfoData!}
                total={total}
                handleCheckout={handleCheckout}
                duration={duration}
                visiblePackages={addOns}
                mainPackage={formWatch.packages as IPackage}
              />
            )}
          </div>
          <ReCAPTCHA ref={recaptchaRef} sitekey="6Lcp4MIZAAAAABHdGwULZhEv01tu2iODEDzSterA" size="invisible" />
        </div>
      </Section>
      <FooterCheckout
        step={step}
        actionBack={() => handleActionBack()}
        action={handleAction}
        disabled={nextBtnDisabled || !mainPackage?.id}
      />
    </>
  );
};

export default Checkout;
